import React from 'react';
import { motion } from 'framer-motion';
import { PhoneCallIcon, CheckIcon } from 'lucide-react';
import { usePrefersReducedMotion } from './motion';
export type TranscriptLine = {
  from: 'caller' | 'ai' | 'customer' | 'system';
  text: string;
};
type DeviceMockupProps = {
  mode: 'call' | 'chat';
  heading: string;
  lines: TranscriptLine[];
  resultChip?: string;
};

/* Literal "what it looks like" mockups, not decoration -- a stylized phone
   frame showing either a live call transcript or an automated text thread.
   Reused twice: AIReceptionist (mode="call") and the Automations feature
   section (mode="chat"), each with real product copy, not invented dialogue
   beyond what the existing site copy already promises. Matches the site's
   established visual language (dark surfaces, emerald accent, monospace
   labels, framer-motion whileInView reveals) rather than introducing a new
   style. Continuous animation (the ring pulse) respects reduced motion. */
export function DeviceMockup({
  mode,
  heading,
  lines,
  resultChip
}: DeviceMockupProps) {
  const reducedMotion = usePrefersReducedMotion();
  return <div className="mx-auto w-full max-w-sm overflow-hidden rounded-[2rem] border border-[#232323] bg-[#0d0d0d] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]" data-id="element-741" data-mp="2o7exn0coi3jw">
      {/* status bar */}
      <div className="flex items-center justify-between border-b border-[#1c1c1c] bg-[#0a0a0a] px-5 py-3.5" data-id="element-742" data-mp="2x8mqv40c8e74">
        <div className="flex items-center gap-2.5" data-id="element-743" data-mp="29isfkqvo2xoz">
          {mode === 'call' ? <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/15" data-id="element-744" data-mp="25iib5iniwfc8">
              {!reducedMotion && <motion.span className="absolute inset-0 rounded-full bg-emerald-500/25" animate={{
            scale: [1, 1.9],
            opacity: [0.6, 0]
          }} transition={{
            duration: 1.6,
            repeat: Infinity,
            ease: 'easeOut'
          }} aria-hidden="true" data-id="element-745" data-mp="2jaoicf8p7bzj" />}
              <PhoneCallIcon className="h-3 w-3 text-emerald-500" aria-hidden="true" data-id="element-746" data-mp="3lw8sgeq8umbq" />
            </span> : <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" data-id="element-747" data-mp="3qusjuunlwqbh" />}
          <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-neutral-400" data-id="element-748" data-mp="3vlio24kaqn4l">
            {heading}
          </span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-600" data-id="element-749" data-mp="4exx0ku8rf7k">
          Live
        </span>
      </div>

      {/* transcript */}
      <div className="space-y-3 px-5 py-6" data-id="element-750" data-mp="37vocrrfml6mg">
        {lines.map((line, i) => {
        const isSelf = line.from === 'ai' || line.from === 'system';
        if (mode === 'call') {
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 8
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.35,
            delay: i * 0.35
          }} data-id="element-751" data-mp="3gy7e273rjb6l">
                <p className={`font-mono text-[10px] uppercase tracking-[0.15em] ${isSelf ? 'text-emerald-500' : 'text-neutral-600'}`} data-id="element-752" data-mp="2gp7ntx28pwhq">
                  {line.from === 'ai' ? 'AI receptionist' : 'Caller'}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-neutral-200" data-id="element-753" data-mp="22xknnm56g3to">{line.text}</p>
              </motion.div>;
        }
        return <motion.div key={i} initial={{
          opacity: 0,
          y: 8
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.3,
          delay: i * 0.3
        }} className={`flex ${isSelf ? 'justify-end' : 'justify-start'}`} data-id="element-754" data-mp="1y01bcrs1i14d" data-mpi={i}>
              <p className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${isSelf ? 'rounded-br-sm bg-emerald-500 text-[#0a0a0a]' : 'rounded-bl-sm bg-[#1c1c1c] text-neutral-200'}`} data-id="element-755" data-mp="3jbj84urmofj9">
                {line.text}
              </p>
            </motion.div>;
      })}

        {resultChip && <motion.div initial={{
        opacity: 0,
        y: 8
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-40px'
      }} transition={{
        duration: 0.35,
        delay: lines.length * (mode === 'call' ? 0.35 : 0.3) + 0.15
      }} className="mt-2 flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-2.5" data-id="element-756" data-mp="221xa53wj91py">
            <CheckIcon className="h-3.5 w-3.5 shrink-0 text-emerald-500" aria-hidden="true" data-id="element-757" data-mp="1jbo23de2efqc" />
            <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-emerald-400" data-id="element-758" data-mp="1nd9k1d5ghk7e">
              {resultChip}
            </span>
          </motion.div>}
      </div>
    </div>;
}